import {
  LightLoginProps,
  MultiCruiseSelectedInfo,
  UserSessionsBase,
  ValidationInputI,
} from '@msc-b2c/interfaces/types';
import './lightlogin.module.scss';
import {
  closeLoader,
  createNewSession,
  getActiveSession,
  getDateFormat,
  getKeyboardType,
  getLightLoginPassengerMinimumAge,
  LightLoginApi,
  LightLoginUpgrade,
  mscGet,
  mscRemove,
  openLoader,
  removeSessionsOfCurrentTab,
  replacePlaceholders,
  useLazyGetBookingQuery,
  validateFormField,
} from '@msc-b2c/shared-utils';
import {
  CabinI,
  GetBookingMyMscResponse,
  LightLoginRequest,
  LightLoginRequestForm,
} from '@msc-b2c/interfaces/interfaces';
import {
  BaseSelect,
  Button,
  DatePickerInput,
  Icon,
  InputText,
  Modal,
  Text,
} from '@msc-b2c/shared-components';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Controller, FieldErrors, FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import {
  getCabinByPassengerId,
  getDeviceType,
  getMultiCruise,
  getMultiCruiseSelect,
  getPassengerbyId,
} from '../utils/utils';
import { calculateRoute } from '../navigationmenu/utils/navigationMenuUtils';
import { Recaptcha } from '../recaptcha/Recaptcha';
import {
  setPendingBookingStatusAnalytics,
  usePrivateAreaPageViewTracking,
} from '../utils/analyticsUtils';
import useDirectLogin, { DIRECT_LOGIN_TYPE } from './useDirectLogin';

const LIGHT_LOGIN_FIELD_ORDER = ['bookingNumber', 'name', 'surname'] as const;

const isFieldRequired = (validationRules?: ValidationInputI[]) =>
  validationRules?.some((rule) => rule.validationRules === 'vr-mandatory-field') ?? false;

export function LightLogin(lightLoginProps: Readonly<LightLoginProps>) {
  const dateInputFormat = getDateFormat();
  const [showDuplicateName, setShowDuplicateName] = useState(false);
  const [mulesoftToken, setMulesoftToken] = useState<string>('');
  const [redirectPath, setRedirectPath] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const [showMultiCruise, setShowMultiCruise] = useState(false);
  const [multiCruiseArray, setMultiCruiseArray] = useState<{ value: string; text: string }[]>();
  const [multiCruisePreSelect, setMultiCruisePreSelect] = useState<string>('');
  const [cruiseId, setCruiseId] = useState<string>('');
  const [userSession, setUserSession] = useState<UserSessionsBase>();

  const isMultiCruiseRequiredConfig = lightLoginProps.multiCruiseValidationRule.required;
  const minimumAgeDateOfBirth = getLightLoginPassengerMinimumAge();
  const disableCheckAgePopupLightLogin = !lightLoginProps.disableCheckAgePopup;

  // NEW
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [pendingSubmit, setPendingSubmit] = useState<{
    data: LightLoginRequest | LightLoginRequestForm;
    redirect?: string;
  } | null>(null);
  const [isPriceType, setIsPriceType] = useState(false);
  const [isMultiCruise, setIsMultiCruise] = useState(false);
  const [isMinimumAge, setIsMinimumAge] = useState(false);
  const [isDepartureDate, setIsDepartureDate] = useState(false);
  const [isFreezeOption, setIsFreezeOption] = useState(false);
  const [isAgency, setIsAgency] = useState(false);
  const [isClosedCruisePopup, setIsClosedCruisePopup] = useState(false);
  const [isOngoingCruisePopup, setIsOngoingCruisePopup] = useState(false);

  const {
    directLoginType,
    isConfirmationPopup,
    isRecaptchaDisabled,
    lightLoginParams,
    blankFormOnError,
    isDefaultRedirect,
    getRedirectURL,
    getFallbackURL,
    onConfirmationPopupSubmit,
    onLoginError,
    invalidateDirectLogin,
  } = useDirectLogin(lightLoginProps.demFallbackLink, lightLoginProps.prepaidPagesMappedURLs);
  usePrivateAreaPageViewTracking();

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    clearErrors,
    setError,
    control,
    setValue,
  } = useForm({ shouldFocusError: false });

  const focusFirstInvalidField = useCallback(
    (fieldErrors?: FieldErrors) => {
      const formErrors = fieldErrors ?? errors;

      for (const fieldName of LIGHT_LOGIN_FIELD_ORDER) {
        if (formErrors[fieldName]) {
          document.getElementById(`${fieldName}-1`)?.focus();
          return;
        }
      }
    },
    [errors]
  );

  useEffect(() => {
    if (lightLoginParams.name) setValue('name', lightLoginParams.name);
    if (lightLoginParams.bookingNumber) setValue('bookingNumber', lightLoginParams.bookingNumber);
    if (lightLoginParams.surname) setValue('surname', lightLoginParams.surname);
  }, [setValue]);

  const resetRecaptcha = () => {
    // Refresh reCAPTCHA after an error occurs: this is useful for ensuring the user can re-attempt the reCAPTCHA challenge
    // after a failed login attempt, so the next submit gets a new token
    setRecaptchaToken('');
    const grecaptcha =
      globalThis.window !== undefined
        ? (
            globalThis as {
              grecaptcha?: { reset: () => void };
            }
          ).grecaptcha
        : undefined;
    grecaptcha?.reset?.();
  };

  useEffect(() => {
    if (isLoading) {
      openLoader('.loader');
    } else {
      closeLoader('.loader');
    }
  }, [isLoading]);

  const executeLogin = async (
    data: LightLoginRequest | LightLoginRequestForm,
    redirect: string | undefined,
    token: string
  ) => {
    let lightLoginRequestNew: LightLoginRequest = {
      name: data.name,
      surname: data.surname,
      bookingNumber: data.bookingNumber,
      recaptchaToken: token,
    };
    if ('customer' in data) {
      lightLoginRequestNew = {
        name: data.name,
        surname: data.surname,
        bookingNumber: data.bookingNumber,
        dateOfBirth: data.customer?.dateOfBirth,
        recaptchaToken: token,
      };
    }

    if (directLoginType === DIRECT_LOGIN_TYPE.DEM) {
      setRecaptchaToken('');
      delete lightLoginRequestNew.recaptchaToken;
      lightLoginRequestNew = {
        ...lightLoginRequestNew,
        signature: data.signature,
        signatureDate: data.signatureDate,
        cruiseId: data.cruiseId,
      };
    } else if (directLoginType === DIRECT_LOGIN_TYPE.BF) {
      lightLoginRequestNew = {
        name: data.name,
        surname: data.surname,
        bookingNumber: data.bookingNumber,
        recaptchaToken: token,
      };
      if ('customer' in data && data.customer?.dateOfBirth) {
        lightLoginRequestNew.dateOfBirth = data.customer.dateOfBirth;
      }
    }

    let updateCruiseId: string | undefined = '';
    let lightLoginUpgrade = false;
    if ('cruiseId' in data && directLoginType === DIRECT_LOGIN_TYPE.NONE) {
      updateCruiseId = data.cruiseId;
      setCruiseId(data.cruiseId ?? '');
      lightLoginUpgrade = true;
    }
    setIsLoading(true);

    if (lightLoginUpgrade) {
      const lightLoginUpgradeAPI = new LightLoginUpgrade(
        false,
        false,
        { cruiseId: updateCruiseId ?? '' },
        ''
      );
      lightLoginUpgradeAPI
        .sendData()
        .then((response) => {
          setUserSession({
            token: response.token,
            email: '',
            loginMode: 'light',
            bookingNumber: response.bookingNumber,
            cruiseId: response.cruiseId,
            crmId: undefined,
            goldenId: undefined,
            passengerId: response.passengerId,
            source: 'mymsc',
          });
          createNewSession(
            response.token,
            'light',
            'mymsc',
            '',
            undefined,
            undefined,
            response.bookingNumber,
            response.cruiseId,
            undefined,
            undefined,
            response.passengerId
          );
          setShowMultiCruise(false);
          setIsMultiCruise(false);
          manageRedirectAfter(redirect);
          setMulesoftToken(response.token);
        })
        .catch((error) => {
          setIsLoading(false);
          const apiError = error.code;
          const issueCode = error.issue_code;
          const causes = error.causes;
          manageError(apiError, issueCode, causes);
        });
    } else {
      const lightLoginApi = new LightLoginApi(false, false, lightLoginRequestNew, '');
      lightLoginApi
        .sendData()
        .then((response) => {
          setUserSession({
            token: response.token,
            email: '',
            loginMode: 'light',
            bookingNumber: response.bookingNumber,
            cruiseId: showMultiCruise ? updateCruiseId : response.cruiseId ?? cruiseId,
            crmId: undefined,
            goldenId: undefined,
            passengerId: response.passengerId,
            source: 'mymsc',
          });
          createNewSession(
            response.token,
            'light',
            'mymsc',
            '',
            undefined,
            undefined,
            response.bookingNumber,
            showMultiCruise ? updateCruiseId : response.cruiseId ?? cruiseId,
            undefined,
            undefined,
            response.passengerId
          );
          manageRedirectAfter(redirect);
          if (!response.cruiseId) {
            setShowMultiCruise(true);
          }
          setMulesoftToken(response.token);
        })
        .catch((error) => {
          setIsLoading(false);
          const apiError = error.code;
          const issueCode = error.issue_code;
          const causes = error.causes;
          manageError(apiError, issueCode, causes);
          onLoginError();
          if (blankFormOnError) {
            setValue('name', '');
            setValue('surname', '');
            setValue('bookingNumber', '');
          }
        });
    }
  };

  useEffect(() => {
    if (isConfirmationPopup) {
      setModalType('CONFIRMATION');
    }
  }, [isConfirmationPopup]);

  /**
   * Handles the form submission for LightLogin.
   * If the reCAPTCHA token is not available, it sets the pending submit state and triggers the reCAPTCHA challenge.
   * Once the token is available, it proceeds to execute the login.
   *
   * @param data - The form data, either LightLoginRequest or LightLoginRequestForm.
   * @param redirect - Optional redirect path after successful login.
   */
  const onSubmitForm = async (
    data: LightLoginRequest | LightLoginRequestForm,
    redirect?: string
  ) => {
    if (!recaptchaToken && !isRecaptchaDisabled) {
      setPendingSubmit({ data, redirect });
      // For invisible reCAPTCHA v2, this will trigger the challenge and callback
      grecaptcha.execute();
      return;
    }

    await executeLogin(data, redirect, recaptchaToken);
  };

  const manageRedirectAfter = (redirect?: string) => {
    if (isDefaultRedirect.current || !redirect) {
      switch (getDeviceType()) {
        case 'desktop':
          redirectPath === '' &&
            setRedirectPath(
              calculateRoute(
                lightLoginProps.myAreaHomepage,
                lightLoginProps.desktopLandingLink?.url ?? lightLoginProps.myAreaHomepage
              )
            );
          break;
        case 'mobile':
          redirectPath === '' &&
            setRedirectPath(
              calculateRoute(
                lightLoginProps.myAreaHomepage,
                lightLoginProps.mobileLandingLink?.url ?? lightLoginProps.myAreaHomepage
              )
            );
          break;
        case 'tablet':
          redirectPath === '' &&
            setRedirectPath(
              calculateRoute(
                lightLoginProps.myAreaHomepage,
                lightLoginProps.tabletLandingLink?.url ?? lightLoginProps.myAreaHomepage
              )
            );
          break;
      }
    }
  };

  const manageError = (apiError: number, issueCode: string, causes: string) => {
    if (!isRecaptchaDisabled) {
      resetRecaptcha();
    }

    switch (true) {
      case apiError === 403 && causes === 'date_of_birth_required':
        setModalType(null);
        setShowDuplicateName(true);
        break;
      case apiError === 403 && causes === 'booking_office_mismatch':
      case apiError === 403 && causes === 'invalid_office_code':
        setModalType(null);
        removeSessionsOfCurrentTab();
        setModalType('COUNTRYNOTMATCH');
        break;
      case apiError === 403 && causes === 'invalid_price_type':
        setModalType(null);
        removeSessionsOfCurrentTab();
        setIsPriceType(true);
        setModalType('PRICE_TYPE');
        break;
      case apiError === 403 && causes === 'age_below_minimum':
        setModalType(null);
        removeSessionsOfCurrentTab();
        setIsMinimumAge(true);
        setModalType('AGEMINIMUMAGELIMIT');
        break;
      case apiError === 403 && causes === 'cruise_departure_too_soon':
        setModalType(null);
        removeSessionsOfCurrentTab();
        setIsDepartureDate(true);
        setModalType('DEPARTUREDATE');
        break;
      case apiError === 403 && causes === 'cruise_ongoing':
        setModalType(null);
        removeSessionsOfCurrentTab();
        setIsOngoingCruisePopup(true);
        setModalType('CRUISEONGOING');
        break;
      case apiError === 403 && causes === 'cruise_completed':
        setModalType(null);
        removeSessionsOfCurrentTab();
        setIsClosedCruisePopup(true);
        setModalType('CRUISECLOSED');
        break;
      case apiError === 403 && causes === 'option_bookings_disabled':
        setModalType(null);
        removeSessionsOfCurrentTab();
        setIsFreezeOption(true);
        setModalType('FREEZEOPTION');
        break;
      case apiError === 403 && causes === 'invalid_agency_id':
        setModalType(null);
        removeSessionsOfCurrentTab();
        setIsAgency(true);
        setModalType('AGENCYISNOTELIGIBLE');
        break;
      default:
        removeSessionsOfCurrentTab();
        setModalType('ERROR');
        break;
    }
  };

  const getContentByModalType = (modalCategory: string | null) => {
    const modalContent = {
      PRICE_TYPE: {
        iconType: lightLoginProps.iconPriceType,
        title: lightLoginProps.titlePriceType,
        message: lightLoginProps.priceTypeMessage,
        styleOption: '-modal-small',
      },
      CONFIRMATION: {
        iconType: 'none',
        title: lightLoginProps.titleConfirmationPopup,
        message: replacePlaceholders(lightLoginProps.messageConfirmation, {
          name: lightLoginParams.name,
          surname: lightLoginParams.surname,
        }),
        styleOption: '-modal-small',
      },
      ERROR: {
        iconType: lightLoginProps.iconErrorMessageType,
        title: lightLoginProps.titleGenericType,
        message: lightLoginProps.genericErrorMessage,
        styleOption: '-modal-small',
      },
      COUNTRYNOTMATCH: {
        iconType: lightLoginProps.countryNotMatchBanner?.icon,
        title: lightLoginProps.countryNotMatchBanner.title,
        message: lightLoginProps.countryNotMatchBanner.description,
        styleOption: '-modal-large',
      },
      DEPARTUREDATE: {
        iconType: lightLoginProps.departureDateBanner.icon,
        title: lightLoginProps.departureDateBanner.title,
        message: lightLoginProps.departureDateBanner.description,
        styleOption: '-modal-large',
      },
      FREEZEOPTION: {
        iconType: lightLoginProps.freezeOptionBanner.icon,
        title: lightLoginProps.freezeOptionBanner.title,
        message: lightLoginProps.freezeOptionBanner.description,
        styleOption: '-modal-small',
      },
      AGEMINIMUMAGELIMIT: {
        iconType: lightLoginProps.minimumAgeLimitBanner.icon,
        title: lightLoginProps.minimumAgeLimitBanner.title,
        message: lightLoginProps.minimumAgeLimitBanner.description,
        styleOption: '-modal-small',
      },
      AGENCYISNOTELIGIBLE: {
        iconType: lightLoginProps.agencyIsNotEligibleBanner.icon,
        title: lightLoginProps.agencyIsNotEligibleBanner.title,
        message: lightLoginProps.agencyIsNotEligibleBanner.description,
        styleOption: '-modal-small',
      },
      CRUISECLOSED: {
        iconType: lightLoginProps.closeCruiseBanner.icon,
        title: lightLoginProps.closeCruiseBanner.title,
        message: lightLoginProps.closeCruiseBanner.description,
        styleOption: '-modal-small',
      },
      CRUISEONGOING: {
        iconType: lightLoginProps.ongoingCruiseBanner.icon,
        title: lightLoginProps.ongoingCruiseBanner.title,
        message: lightLoginProps.ongoingCruiseBanner.description,
        styleOption: '-modal-small',
      },
    };

    return modalContent[modalCategory ?? 'ERROR'];
  };

  const closeModal = () => {
    if (modalType === 'CONFIRMATION') {
      invalidateDirectLogin();
    }
    setModalType(null);
  };

  const LightLoginModal = () => {
    const modalA11yId = useId();
    const modalTitleId = `${modalA11yId}-title`;
    const modalDescriptionId = `${modalA11yId}-description`;
    const closeIconRef = useRef<HTMLElement>(null);
    const { iconType, title, message, styleOption } = getContentByModalType(modalType);

    const notMeAction =
      directLoginType === DIRECT_LOGIN_TYPE.BF
        ? () => {
            invalidateDirectLogin();
            setModalType(null);
            setValue('name', '');
            setValue('surname', '');
            setValue('bookingNumber', '');
          }
        : undefined;

    return (
      <Modal
        onClose={closeModal}
        styleOption={`${styleOption} -modal-private-area`}
        hideCloseIcon={true}
        ariaLabelledBy={modalTitleId}
        ariaDescribedBy={modalDescriptionId}
        initialFocusRef={closeIconRef}
      >
        <div className="modal-header">
          {iconType && iconType !== 'none' && (
            <Icon
              styleOption="modal-header__icon"
              icon={iconType}
              ariaHidden={true}
            />
          )}
          <Text
            id={modalTitleId}
            styleOption="modal-error-title"
            text={title}
          />
          <Icon
            ref={closeIconRef}
            styleOption="modal-header__close-icon"
            icon="close"
            onClick={closeModal}
            ariaLabel="Close"
          />
        </div>
        <div
          className="modal-error"
          id={modalDescriptionId}
        >
          <Text text={message} />
        </div>
        {modalType === 'CONFIRMATION' && (
          <div className="button-action-container">
            <Button
              styleOption="cmp-button cmp-button__secondary isNotMe-button"
              text={lightLoginProps.notMeLabel}
              buttonLink={notMeAction ? undefined : lightLoginProps.notMeLink}
              accessibilityLabel={lightLoginProps.notMeLabel}
              onClick={notMeAction}
            />
            <Button
              styleOption="cmp-button__primary cmp-button confirm-button"
              buttonType="button"
              text={lightLoginProps.confirmLabel}
              accessibilityLabel={lightLoginProps.confirmLabel}
              disabled={isLoading}
              onClick={() => submitConfirmation()}
            />
          </div>
        )}
        {modalType === 'COUNTRYNOTMATCH' && (
          <div className="button-action-container">
            <Button
              styleOption="cmp-button__primary cmp-button confirm-button"
              text={lightLoginProps.countryNotMatchBanner.buttonLabel}
              accessibilityLabel={lightLoginProps.countryNotMatchBanner.buttonLabel}
              buttonLink={lightLoginProps.countryNotMatchBanner.link}
            />
          </div>
        )}
        {modalType === 'DEPARTUREDATE' && (
          <div className="button-action-container">
            <Button
              styleOption="cmp-button__primary cmp-button confirm-button"
              text={lightLoginProps.departureDateBanner.buttonLabel}
              accessibilityLabel={lightLoginProps.departureDateBanner.buttonLabel}
              buttonLink={lightLoginProps.departureDateBanner.link}
            />
          </div>
        )}
      </Modal>
    );
  };

  const submitConfirmation = async () => {
    onConfirmationPopupSubmit();

    const redirectURL = getRedirectURL();
    const fallbackURL = getFallbackURL();
    if (redirectURL) {
      setRedirectPath(redirectURL);
    } else {
      setRedirectPath(fallbackURL ?? '');
    }

    await onSubmitForm(lightLoginParams, redirectURL ?? fallbackURL);
  };

  const [fetchBooking] = useLazyGetBookingQuery();

  const getBookingNumber = (bookingNumber?: string) => {
    if (directLoginType === DIRECT_LOGIN_TYPE.BF) return lightLoginParams.bookingNumber;

    return bookingNumber ?? '';
  };

  useEffect(() => {
    if (!mulesoftToken) return;
    const { passengerId, bookingNumber } = getActiveSession();

    fetchBooking(getBookingNumber(bookingNumber))
      .then(async ({ data }) => {
        const booking: GetBookingMyMscResponse | undefined = data;
        if (booking) {
          const loggedPassenger = getPassengerbyId(passengerId, booking?.passengers);
          const cabinsByPassengerId: CabinI | undefined = getCabinByPassengerId(
            booking?.cabins,
            passengerId ?? ''
          );
          const cruiseIdFromData = cabinsByPassengerId?.cruise.id ?? '';

          let isOngoingCruise = false;
          let isClosedCruise = false;

          if (showMultiCruise) {
            const [isMultiCabin, cabins] = getMultiCruise(booking?.cabins);
            if (isMultiCabin) {
              const multiCruiseInfo: MultiCruiseSelectedInfo = await getMultiCruiseSelect(
                cabins,
                lightLoginProps.formatMultiCruiseDropdownPlaceholder,
                getDateFormat()
              );
              if (multiCruiseInfo.items.length === 1) {
                setIsMultiCruise(true);
                if (userSession) {
                  createNewSession(
                    userSession.token,
                    'light',
                    'mymsc',
                    '',
                    loggedPassenger?.name,
                    loggedPassenger?.surname,
                    userSession.bookingNumber,
                    multiCruiseInfo.items[0].value,
                    undefined,
                    undefined,
                    userSession.passengerId
                  );
                }
                await executeLogin(
                  {
                    name: loggedPassenger?.name ?? '',
                    surname: loggedPassenger?.surname ?? '',
                    bookingNumber: userSession?.bookingNumber ?? '',
                    cruiseId: multiCruiseInfo.items[0].value,
                    recaptchaToken: recaptchaToken,
                  },
                  redirectPath,
                  recaptchaToken
                );
              } else if (multiCruiseInfo.items.length > 1) {
                setIsMultiCruise(true);
                setModalType(null);
                setShowMultiCruise(true);
                setMultiCruiseArray(multiCruiseInfo.items);
                setMultiCruisePreSelect(multiCruiseInfo.items[0].value);
                setIsLoading(false);
              } else {
                if (multiCruiseInfo.isClosedCruise && !multiCruiseInfo.isOngoingCruise) {
                  setIsClosedCruisePopup(true);
                  removeSessionsOfCurrentTab();
                  setIsLoading(false);
                  setModalType('CRUISECLOSED');
                }
                if (!multiCruiseInfo.isClosedCruise && multiCruiseInfo.isOngoingCruise) {
                  setIsOngoingCruisePopup(true);
                  removeSessionsOfCurrentTab();
                  setIsLoading(false);
                  setModalType('CRUISEONGOING');
                }
              }
            }
          }

          if (isClosedCruise && !isMultiCruise) {
            setIsClosedCruisePopup(true);
            removeSessionsOfCurrentTab();
            setIsLoading(false);
            setModalType('CRUISECLOSED');
          }

          if (isOngoingCruise && !isMultiCruise) {
            setIsOngoingCruisePopup(true);
            removeSessionsOfCurrentTab();
            setIsLoading(false);
            setModalType('CRUISEONGOING');
          }
          if (
            !isPriceType &&
            !isMultiCruise &&
            !isMinimumAge &&
            !isDepartureDate &&
            !isFreezeOption &&
            !isAgency &&
            !isClosedCruisePopup &&
            !isOngoingCruisePopup &&
            !showMultiCruise
          ) {
            if (userSession) {
              createNewSession(
                userSession.token,
                'light',
                'mymsc',
                '',
                loggedPassenger?.name,
                loggedPassenger?.surname,
                userSession.bookingNumber,
                userSession.cruiseId && userSession.cruiseId !== ''
                  ? userSession.cruiseId
                  : cruiseIdFromData,
                undefined,
                undefined,
                userSession.passengerId
              );
            }
            setPendingBookingStatusAnalytics();
            const jwtReturnUrl = mscGet<string>('session', 'mscJwtExpiredReturnUrl');
            if (jwtReturnUrl) {
              mscRemove('session', 'mscJwtExpiredReturnUrl');
              globalThis.location.href = jwtReturnUrl;
            } else {
              if (directLoginType === DIRECT_LOGIN_TYPE.BF) {
                const redirectURL = getRedirectURL(booking);
                if (redirectURL) {
                  globalThis.location.href = redirectURL;

                  return;
                }
              }
              globalThis.location.href = redirectPath;
            }
          }
        } else {
          removeSessionsOfCurrentTab();
          setModalType('ERROR');
          setIsLoading(false);
        }
      })
      .catch(() => {
        removeSessionsOfCurrentTab();
        setModalType('ERROR');
      });
  }, [fetchBooking, mulesoftToken, redirectPath]);

  const handleBookingNumberChange = () => {
    setShowDuplicateName(false);
    setShowMultiCruise(false);
  };

  const handleRecaptchaResponse = (data: string) => {
    setRecaptchaToken(data);

    // If the user already submitted the form and we were waiting for the token,
    // continue with the login flow now that we have it.
    if (pendingSubmit) {
      void executeLogin(pendingSubmit.data, pendingSubmit.redirect, data);
      setPendingSubmit(null);
    }
  };

  return (
    <div className="cmp-light-login-wrapper">
      <form
        className="cmp-form cmp-light-login"
        onSubmit={handleSubmit(onSubmitForm as SubmitHandler<FieldValues>, focusFirstInvalidField)}
        {...(modalType ? { inert: '' } : {})}
      >
        <InputText
          inputMode={getKeyboardType(
            lightLoginProps.validationRulesBookingNumberContainerCollection
          )}
          register={register}
          errors={errors}
          index={1}
          label={lightLoginProps.bookingNumberLabelPlaceholder}
          placeholder={lightLoginProps.bookingNumberLabelPlaceholder}
          clearErrors={clearErrors}
          handleInputChange={handleBookingNumberChange}
          name={'bookingNumber'}
          required={isFieldRequired(
            lightLoginProps.validationRulesBookingNumberContainerCollection
          )}
          clearErrorsOnFocus={false}
          validation={{
            validate: (value) => {
              return validateFormField(
                value,
                lightLoginProps.validationRulesBookingNumberContainerCollection || []
              );
            },
            onBlur: () => {
              trigger(`bookingNumber`);
            },
          }}
        ></InputText>
        <InputText
          register={register}
          errors={errors}
          index={1}
          label={lightLoginProps.firstNameLabelPlaceholder}
          placeholder={lightLoginProps.firstNameLabelPlaceholder}
          clearErrors={clearErrors}
          name={'name'}
          required={isFieldRequired(lightLoginProps.validationRulesFirstNameContainerCollection)}
          clearErrorsOnFocus={false}
          validation={{
            validate: (value) => {
              return validateFormField(
                value,
                lightLoginProps.validationRulesFirstNameContainerCollection || []
              );
            },
            onBlur: () => {
              trigger(`name`);
            },
          }}
        ></InputText>
        <InputText
          register={register}
          errors={errors}
          index={1}
          label={lightLoginProps.lastNameLabelPlaceholder}
          placeholder={lightLoginProps.lastNameLabelPlaceholder}
          clearErrors={clearErrors}
          name={'surname'}
          required={isFieldRequired(lightLoginProps.validationRulesLastNameContainerCollection)}
          clearErrorsOnFocus={false}
          validation={{
            validate: (value) => {
              return validateFormField(
                value,
                lightLoginProps.validationRulesLastNameContainerCollection || []
              );
            },
            onBlur: () => {
              trigger(`surname`);
            },
          }}
        ></InputText>
        {showDuplicateName && (
          <DatePickerInput
            key={'date-field-form-customer-dateOfBirth'}
            control={control}
            errors={errors}
            index={1}
            setError={setError}
            label={lightLoginProps.dateOfBirthLabelPlaceholder}
            setValue={setValue}
            clearErrors={clearErrors}
            inputTypeDate={dateInputFormat}
            name={'customer.dateOfBirth'}
            disableCheckAgePopup={lightLoginProps.disableCheckAgePopup}
            minAge={minimumAgeDateOfBirth}
            validation={{
              validate: (value: string) => {
                return validateFormField(
                  value,
                  lightLoginProps.validationRulesDateOfBirthContainerCollection || [],
                  { minimumAgeDateOfBirth, disableCheckAgePopupLightLogin }
                );
              },
              onBlur: () => {
                trigger(`dateOfBirth`);
              },
            }}
            disabled={false}
          ></DatePickerInput>
        )}
        {showMultiCruise && multiCruiseArray && (
          <Controller
            control={control}
            name={'cruiseId'}
            rules={{
              required: !(isMultiCruiseRequiredConfig || multiCruisePreSelect),
            }}
            defaultValue={multiCruisePreSelect}
            render={({ field }) => {
              return (
                <BaseSelect
                  key={`cruiseId`}
                  label={lightLoginProps.multiCruisePlaceholder}
                  placeholder={lightLoginProps.multiCruisePlaceholder}
                  ariaLabel={lightLoginProps.multiCruisePlaceholder}
                  styleOption={'cruise-select'}
                  items={multiCruiseArray}
                  errors={errors}
                  value={String(multiCruisePreSelect || '')}
                  fieldName={field.name}
                  onChange={field.onChange}
                  onBlur={() => trigger(`cruiseId`)}
                  clearErrors={clearErrors}
                  disabled={false}
                />
              );
            }}
          ></Controller>
        )}
        <div dangerouslySetInnerHTML={{ __html: lightLoginProps.termAndCondition }} />
        <Recaptcha
          manageTokenRecaptcha={handleRecaptchaResponse}
          config={lightLoginProps.googleReCaptchaServiceConfig}
          isDisabled={isRecaptchaDisabled}
        ></Recaptcha>
        <Button
          styleOption="cmp-button__primary -extended"
          buttonType="submit"
          disabled={isLoading}
          text={lightLoginProps.submitLabelPlaceholder}
        />
      </form>
      {modalType && (
        <>
          <div className="overlay" />
          <LightLoginModal />
        </>
      )}
    </div>
  );
}
